import axios from "axios";
import { toast } from "sonner";
import { enqueue, isNetworkError } from "@/lib/offlineQueue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("impo_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---------------------------------------------------------------------------
// Service-worker cache invalidation.
//
// The SW uses stale-while-revalidate for read endpoints (see /public/sw.js).
// After any write, the very next GET can transiently return a stale snapshot
// that's missing the just-created / updated / deleted item — which manifests
// to users as "I saved the doc but it doesn't show up until I refresh."
//
// Fix: whenever a mutation succeeds, message the SW to drop the cached list
// entries for the corresponding read endpoint. Also do an optimistic UI update
// in the calling component; this SW purge is a belt + suspenders safeguard
// that also fixes the bug across pages we don't touch individually.
// ---------------------------------------------------------------------------
const WRITE_METHODS = new Set(["post", "put", "patch", "delete"]);

// Map a mutation URL (e.g. "/documents/abc") back to the collection root(s)
// whose SW cache should be flushed ("/api/documents"). We include a few
// closely-related endpoints because a single write often affects them:
// e.g. favoriting a note should also refresh /api/favorites, and scan-to-PDF
// creates a document so it must flush /api/documents.
function _apiPathsForUrl(url) {
  if (!url) return [];
  const path = url.startsWith("http") ? new URL(url).pathname : (url.startsWith("/api") ? url : `/api${url}`);
  const first = path.split("?")[0].split("/").slice(0, 3).join("/"); // "/api/<root>"
  const out = new Set();
  if (first && first !== "/api") out.add(first);
  // Cross-endpoint invalidations for surfaces that aggregate other collections.
  if (first === "/api/favorites-toggle" || first === "/api/favorites") out.add("/api/favorites");
  if (first === "/api/archive") out.add("/api/archived");
  if (first === "/api/pins") out.add("/api/pins");
  // Scan-to-PDF creates a Document, so we must flush the /api/documents list.
  if (first === "/api/scans") out.add("/api/documents");
  // Household share mutations may affect item lists too — refresh household.
  if (first === "/api/households") out.add("/api/households");
  // Media & document uploads can implicitly create a folder (via ?folder_name=),
  // so flush /api/folders whenever we mutate one of the folder-scoped surfaces.
  if (["/api/photos", "/api/videos", "/api/documents"].includes(first)) {
    out.add("/api/folders");
  }
  // Explicit folder writes also flush the aggregate media surfaces so
  // rename/delete/move operations propagate immediately.
  if (first === "/api/folders") {
    out.add("/api/photos");
    out.add("/api/videos");
    out.add("/api/documents");
  }
  return Array.from(out);
}

export function invalidateApiCache(paths) {
  try {
    const arr = Array.isArray(paths) ? paths : [paths];
    if (!arr.length) return;
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "invalidate-api", paths: arr });
    }
  } catch {
    /* silent — cache invalidation is best-effort */
  }
}

// ---------------------------------------------------------------------------
// Offline write queue: on network error, transparently persist the request in
// IndexedDB and resolve the caller with a synthetic response so optimistic UI
// can proceed. Only JSON POST/PATCH/PUT/DELETE calls are queueable; GETs and
// multipart uploads still fail loudly (as the caller would expect).
// ---------------------------------------------------------------------------
const QUEUEABLE_METHODS = new Set(["post", "patch", "put", "delete"]);

let _offlineToastLastShown = 0;
function _showOfflineToast() {
  const now = Date.now();
  if (now - _offlineToastLastShown < 4000) return;
  _offlineToastLastShown = now;
  toast("Saved offline", {
    description: "We'll sync automatically when you're back online.",
  });
}

api.interceptors.response.use(
  (r) => {
    try {
      const method = (r?.config?.method || "get").toLowerCase();
      if (WRITE_METHODS.has(method) && r?.status && r.status >= 200 && r.status < 300) {
        invalidateApiCache(_apiPathsForUrl(r.config?.url));
      }
    } catch { /* noop */ }
    return r;
  },
  async (err) => {
    const cfg = err?.config || {};
    const method = (cfg.method || "get").toLowerCase();

    if (isNetworkError(err) && QUEUEABLE_METHODS.has(method)) {
      // Skip file uploads (Content-Type multipart); those cannot be reliably
      // serialized into IndexedDB.
      const ct = (cfg.headers?.["Content-Type"] || cfg.headers?.["content-type"] || "").toString();
      const isMultipart = ct.includes("multipart/form-data");
      const bodyIsPlainJson = !(cfg.data instanceof FormData) && !isMultipart;
      if (bodyIsPlainJson) {
        // Persist to IndexedDB. axios has already resolved the request config
        // baseURL + url — rebuild the relative URL so replay uses the same base.
        const url = cfg.url && !cfg.url.startsWith("http") ? cfg.url : cfg.url?.replace(API_BASE, "");
        try {
          let body = cfg.data;
          if (typeof body === "string") {
            try { body = JSON.parse(body); } catch { /* leave as string */ }
          }
          const qid = await enqueue({
            method,
            url,
            body,
            // Only persist request-scoped headers we control; auth is picked up
            // at replay time from localStorage, so we don't snapshot it here.
            headers: {},
          });
          _showOfflineToast();
          // Synthetic response — caller sees a resolved promise with a marker.
          const echo = (body && typeof body === "object") ? { ...body } : {};
          if (method === "post" && !echo.id) echo.id = `pending-${qid}`;
          echo.__offline = true;
          echo.__queue_id = qid;
          return {
            data: echo,
            status: 202,
            statusText: "Accepted (queued offline)",
            headers: {},
            config: cfg,
            __offline: true,
          };
        } catch (queueErr) {
          // If IndexedDB itself failed, fall through to the caller's normal error handling.
          // eslint-disable-next-line no-console
          console.warn("[offline-queue] enqueue failed", queueErr);
        }
      }
    }

    return Promise.reject(err);
  }
);

export function fileUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  return `${BACKEND_URL}${pathOrUrl}`;
}

export async function apiUpload(path, formData, method = "post") {
  const token = localStorage.getItem("impo_token");
  const m = (method || "post").toLowerCase();
  const res = await axios.request({
    method: m,
    url: `${API_BASE}${path}`,
    data: formData,
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  });
  // Purge SW cache for the target collection so the next list GET returns
  // the freshly-uploaded item instead of a pre-write stale snapshot.
  try { invalidateApiCache(_apiPathsForUrl(path)); } catch { /* noop */ }
  return res.data;
}
